<template>
  <div class="cr-toolbar gd-toolbar">
    <div class="cr-toolbar-field gd-field-plan form-group"
         :class="{'has-error': v.insurancePlanId.$error}">
      <label class="form-label">Convênio</label>
      <select class="form-select" v-model="filter.insurancePlanId" @change="$emit('change-plan')">
        <option value="">[Selecione o convênio]</option>
        <option value="ALL">Todos os convênios</option>
        <option v-for="item in insurances" :value="item.id" :key="item.id">{{ item.name }}</option>
      </select>
      <div class="form-input-hint" v-if="v.insurancePlanId.$error">Campo obrigatório</div>
    </div>

    <div class="cr-toolbar-field gd-field-period form-group" :class="{'has-error': periodError}">
      <label class="form-label">Período <small>(até 3 meses)</small></label>
      <st-date-period
        :start-date.sync="filter.startDate"
        :end-date.sync="filter.endDate"
        :max-months="3"
        :periods="periods"
        :start-date-validator="v.startDate"
        :end-date-validator="v.endDate"
        @blur:startDate="v.startDate.$touch()"
        @blur:endDate="v.endDate.$touch()"
      />
    </div>

    <div class="cr-toolbar-field gd-field-professional form-group">
      <label class="form-label">Profissional</label>
      <select class="form-select" v-model="filter.professionalId">
        <option value="">Todos os profissionais</option>
        <option v-for="item in professionals" :value="item.id" :key="item.id">
          {{ item.name }}
        </option>
      </select>
    </div>

    <div class="cr-toolbar-field gd-field-type form-group">
      <label class="form-label">Tipo de guia</label>
      <st-multi-select v-model="filter.types" :items="typeItems"
                       value-key="value" label-key="label"/>
    </div>

    <div class="cr-toolbar-field gd-field-link form-group">
      <label class="form-label">Vínculo</label>
      <select class="form-select" :value="filter.link"
              @change="$emit('change-link', $event.target.value)">
        <option v-for="option in linkOptions" :value="option.value" :key="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="cr-toolbar-field gd-field-batch form-group"
         :class="{tooltip: !!batchHint, 'tooltip-bottom': !!batchHint}"
         :data-tooltip="batchHint">
      <label class="form-label">Lote nº</label>
      <dx-autocomplete
        v-model="filter.batch"
        :source="findBatch"
        label="number"
        track-by="id"
        :debounce="600"
        :disabled="!canSearchBatch"
        placeholder="Número"
        @select="$emit('search')">
        <template v-slot="{ item }">
          <a>Lote {{ item.number }} <small class="text-gray">· {{ item.statusName }}</small></a>
        </template>
      </dx-autocomplete>
    </div>

    <div class="cr-toolbar-field gd-field-status form-group">
      <label class="form-label">Status</label>
      <st-multi-select v-model="filter.statuses" :items="statusItems"
                       :disabled="filter.link === 'none'"
                       value-key="value" label-key="label"/>
    </div>

    <div class="cr-toolbar-field gd-field-search form-group">
      <label class="form-label">Pesquisar</label>
      <input type="text" class="form-input" ref="search" v-model="filter.search"
             placeholder="Paciente, nº da guia, carteirinha"
             @keypress.enter="$emit('search')">
    </div>

    <div class="cr-toolbar-actions gd-field-actions">
      <button class="btn btn-link btn-sm gd-clear" @click="$emit('clear')">Limpar filtros</button>
      <button class="btn btn-secondary btn-icon btn-icon-left gd-search"
              :class="{loading}" :disabled="loading" @click="$emit('search')">
        <fa-icon :icon="['fal', 'search']"/>Pesquisar
      </button>
    </div>
  </div>
</template>

<script>
import { statuses } from '@/data/billing-account-statuses';
import { types } from '@/data/billing-guide-types';

const BATCH_STATUS_NAMES = {
  closed: 'fechado',
  sent: 'enviado',
  in_analysis: 'em análise',
  in_appeal: 'em recurso',
  settled: 'baixado',
  canceled: 'cancelado',
};

const STATUS_LABELS = {
  open: 'Registrada',
  in_batch: 'Em lote',
  closed: 'Encerrada',
};


export default {
  props: {
    filter: {
      type: Object,
      required: true,
    },
    v: {
      type: Object,
      required: true,
    },
    insurances: {
      type: Array,
      default: () => [],
    },
    professionals: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      periods: [
        { value: 'today', text: 'Hoje' },
        { value: 'd:-7', text: 'Últimos 7 dias' },
        { value: 'month', text: 'Este mês' },
        { value: 'month:-1', text: 'Mês passado' },
        { value: 'd:-90', text: 'Últimos 3 meses' },
      ],
      linkOptions: [
        { value: 'none', label: 'Sem lote' },
        { value: 'with', label: 'Com lote' },
        { value: 'all', label: 'Todas' },
      ],
    };
  },
  computed: {
    typeItems() {
      return Object.keys(types).map(key => ({ value: key, label: types[key] }));
    },
    statusItems() {
      return Object.keys(statuses)
        .map(key => ({ value: key, label: STATUS_LABELS[key] || statuses[key] }));
    },
    currentInsurance() {
      return this.insurances.find(item => item.id === this.filter.insurancePlanId) || null;
    },
    canSearchBatch() {
      return !!this.currentInsurance && this.filter.link === 'with';
    },
    batchHint() {
      if (this.canSearchBatch) return null;
      if (!this.currentInsurance) return 'Escolha um convênio para buscar pelo lote';
      return 'Disponível no vínculo Com lote';
    },
    periodError() {
      return this.v.startDate.$error || this.v.endDate.$error;
    },
  },
  methods: {
    findBatch(search) {
      if (!this.canSearchBatch) return Promise.resolve([]);
      const params = {
        insuranceId: this.currentInsurance.insuranceId,
        planId: this.currentInsurance.id,
        search,
        limit: 10,
      };
      return this.$http.get('/billing-batches', { params })
        .then(({ data }) => (data.items || []).map(item => ({
          id: item.id,
          number: String(item.number),
          statusName: BATCH_STATUS_NAMES[item.status] || item.status,
        })))
        .catch(() => []);
    },
    focusSearch() {
      if (this.$refs.search) this.$refs.search.focus();
    },
  },
};
</script>
